import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useTransition } from 'react'
import { OriginType, TRACKING_EVENTS } from '~/lib/constants'
import getSingleRouteHref from '~/lib/get-single-route-href'
import { STORYBLOK_TYPES } from '~/lib/storyblok-types'

import { GID } from '@unlikelystudio/commerce-connector'
import { useCustomerContext } from '@unlikelystudio/react-ecommerce-hooks'

import IconCta, { Icons } from '~/components/UI/IconCta'
import { InlineCtaProps } from '~/components/UI/InlineCta'

import { useTracker } from '~/providers/TrackerProvider'

import { ProductTrackingData } from '~/hooks/tracking/useTrackProductCardsList'
import { useINPTrick } from '~/hooks/useINPTrick'
import useLocale from '~/hooks/useLocale'
import { useAddProductToWishlist } from '~/hooks/wishlist/useAddProductToWishlist'
import { useGetWishList } from '~/hooks/wishlist/useGetWishlist'
import { useRemoveProductToWishlist } from '~/hooks/wishlist/useRemoveProductToWishlist'

import { getReferenceIdFromAdminId } from '~/utils/shopify-ids'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type WishlistIconProps = {
  className?: string
  variantId: GID
  shopifyId: GID
  trackingData?: ProductTrackingData
  productLink: InlineCtaProps
  uuid: string
}

function WishlistIcon({
  className,
  shopifyId,
  variantId,
  trackingData,
  productLink,
  uuid,
}: WishlistIconProps) {
  const locale = useLocale()
  const { customerAccessToken } = useCustomerContext()
  const [isPending, startTransition] = useTransition()

  const { data } = useGetWishList()
  const { mutate: addProduct, isLoading: isAdding } = useAddProductToWishlist(
    {},
  )
  const { mutate: removeProduct, isLoading: isRemoveProductLoading } =
    useRemoveProductToWishlist({})

  const tracker = useTracker()
  const router = useRouter()
  const { hasInteracted, onInteract } = useINPTrick()

  const referenceVariantId = getReferenceIdFromAdminId(variantId)
  const referenceProductId = getReferenceIdFromAdminId(shopifyId)
  const isInWishlist = data?.products?.some(
    (item) => item?.variantId === variantId,
  )

  const triggerEvent = (event: string) => {
    tracker.emit<typeof event>(event, {
      origin: OriginType.DETAIL_VIEW,
      item: trackingData,
    })
  }

  const handleWishlistAction = () => {
    if (isInWishlist) {
      removeProduct({
        productId: referenceProductId,
        variantId: referenceVariantId,
        link: productLink,
        uuid,
      })
    } else {
      triggerEvent(TRACKING_EVENTS.ADD_TO_WISHLIST)
      addProduct({
        productId: referenceProductId,
        variantId: referenceVariantId,
        link: productLink,
        uuid,
      })
    }
  }

  return (
    <IconCta
      className={cx(
        className,
        css.WishlistIcon,
        hasInteracted && css.hasInteracted,
      )}
      icon={Icons.Heart}
      iconClassName={cx(css.icon, { isActive: Boolean(isInWishlist) })}
      loading={isAdding || isRemoveProductLoading || isPending}
      ariaLabel="wish-list-icon"
      onClick={(e) => {
        e.preventDefault()
        startTransition(() => {
          if (!customerAccessToken) {
            void router.push(
              `${getSingleRouteHref(
                STORYBLOK_TYPES.LOGIN,
                locale,
              )}?redirectUrl=${router.asPath}`,
            )
            return
          }
          onInteract(handleWishlistAction)
        })
      }}
    />
  )
}

WishlistIcon.defaultProps = {}

export default WishlistIcon
